import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { 
  Star, 
  Flame, 
  Trophy, 
  Users, 
  Plus, 
  ArrowRight,
  CheckCircle2,
  Clock,
  Medal,
  Award
} from 'lucide-react';
import { getLocalDateString, getLocalTodayDateString } from '../lib/utils';


// Helper function to format rank with ordinal suffix
const formatRank = (rank) => {
  if (!rank) return 'N/A';
  const num = parseInt(rank);
  
  // Handle teens (11, 12, 13) which all end with 'th'
  if (num >= 11 && num <= 13) {
    return `${num}th`;
  }
  
  // Get the last digit to determine ordinal suffix
  const lastDigit = num % 10;
  
  switch (lastDigit) {
    case 1: return `${num}st`;
    case 2: return `${num}nd`;
    case 3: return `${num}rd`;
    default: return `${num}th`;
  }
};

// Helper function to get rank styling based on rank number
const getRankStyling = (rank) => {
  if (!rank) return { color: '#7C3AED', icon: Trophy, size: 'w-5 h-5' };
  
  const num = parseInt(rank);
  if (num === 1) return { color: '#FFD700', icon: Trophy, size: 'w-6 h-6' }; // Gold
  if (num === 2) return { color: '#C0C0C0', icon: Medal, size: 'w-6 h-6' }; // Silver  
  if (num === 3) return { color: '#CD7F32', icon: Award, size: 'w-6 h-6' }; // Bronze
  return { color: '#7C3AED', icon: Trophy, size: 'w-5 h-5' }; // Purple for others
};
// Memoized Child Card Component
const ChildCard = React.memo(({ child, index, rank }) => {
  const rankStyling = getRankStyling(rank);
  return (
    <Card 
      key={child.child_id}
      className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg card-hover animate-slide-up"
      style={{ animationDelay: `${index * 0.1}s` }}
      data-testid={`child-card-${child.child_id}`}
    >
      <CardContent className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <Avatar className="h-16 w-16 border-3 border-[#D4AF37]">
            <AvatarImage src={child.profile_image} />
            <AvatarFallback className={`text-xl font-bold ${
              child.gender === 'Muslim' 
                ? 'bg-[#08513F] text-white' 
                : 'bg-[#E76F51] text-white'
            }`}>
              {child.first_name?.charAt(0)}{child.last_name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <h3 className="text-lg font-bold text-[#264653] font-outfit">
              {child.first_name} {child.last_name}
            </h3>
            <p className="text-sm text-[#6B7280]">
              {child.gender} • Age {child.age}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
            <Star className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-4xl font-bold text-[#08513F] font-outfit">{child.total_points}</p>
            <p className="text-xs text-[#6B7280] mb-1">Points</p>
          </div>
          <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
            <Flame className="w-6 h-6 text-[#E76F51] mx-auto mb-2" />
            <p className="text-4xl font-bold text-[#264653] font-outfit">{child.longest_streak}</p>
            <p className="text-xs text-[#6B7280]">Best Streak</p>
          </div>
          <div className="text-center p-4 bg-[#FFFBEB] rounded-2xl">
            <rankStyling.icon className={`${rankStyling.size} text-[${rankStyling.color}] mx-auto mb-2`} />
            <p className="text-4xl font-bold font-outfit" style={{ color: rankStyling.color }}>
              {formatRank(rank)}
            </p>
            <p className="text-xs text-[#6B7280] mb-1">Rank</p>
          </div>
        </div>

        <Button
          asChild
          className="w-full mt-4 bg-[#08513F] hover:bg-[#064031] text-white rounded-xl"
          data-testid={`submit-task-${child.child_id}`}
        >
          <Link to={`/tasks?child=${child.child_id}`}>
            Submit Task
            <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
});

ChildCard.displayName = 'ChildCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URL
const LOGO_URL = '/AIC-Logo.png';

export default function Dashboard() {
  const { user, getAuthHeaders } = useAuth();
  const [children, setChildren] = useState([]);
  const [childRanks, setChildRanks] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const loginStatus = searchParams.get('login');
    
    if (loginStatus === 'success') {
      toast.success('Successfully signed in with Google!', {
        description: 'Welcome back!',
        duration: 5000,
      });
      
      // Clean up the URL (remove query param)
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [childrenRes, submissionsRes] = await Promise.all([
          fetch(`${API_URL}/api/children`, {
            credentials: 'include',
            headers: getAuthHeaders()
          }),
          fetch(`${API_URL}/api/submissions`, {
            credentials: 'include',
            headers: getAuthHeaders()
          })
        ]);

        if (childrenRes.ok) {
          setChildren(await childrenRes.json());
        }
        if (submissionsRes.ok) {
          setSubmissions(await submissionsRes.json());
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAuthHeaders]);

  // Fetch dense ranks for all children
  useEffect(() => {
    const fetchChildRanks = async () => {
      if (children.length === 0) return;
      
      try {
        const rankPromises = children.map(async (child) => {
          try {
            const response = await fetch(`${API_URL}/api/leaderboard/dense-rank/child/${child.child_id}`);
            if (response.ok) {
              const rankData = await response.json();
              return { child_id: child.child_id, dense_rank: rankData.dense_rank };
            } else {
              console.warn(`Failed to fetch rank for child ${child.child_id}: ${response.status}`);
              return { child_id: child.child_id, dense_rank: null };
            }
          } catch (error) {
            console.warn(`Error fetching rank for child ${child.child_id}:`, error);
            return { child_id: child.child_id, dense_rank: null };
          }
        });
        
        const rankResults = await Promise.all(rankPromises);
        const rankMap = {};
        rankResults.forEach(({ child_id, dense_rank }) => {
          rankMap[child_id] = dense_rank;
        });
        
        setChildRanks(rankMap);
      } catch (error) {
        console.error('Failed to fetch child ranks:', error);
      }
    };

    fetchChildRanks();
  }, [children]);

  const totalPoints = useMemo(() => 
    children.reduce((sum, child) => sum + (child.total_points || 0), 0),
    [children]
  );
  
  const bestStreak = useMemo(() => 
    Math.max(...children.map(c => c.longest_streak || 0), 0),
    [children]
  );
  
  const pendingSubmissions = useMemo(() => 
    submissions.filter(s => s.status === 'submitted').length,
    [submissions]
  );
  
  const approvedToday = useMemo(() => 
    submissions.filter(s => {
      const estDate = getLocalDateString(s.submission_date);
      const today = getLocalTodayDateString();
      return s.status === 'approved' && 
             estDate === today;
    }).length,
    [submissions]
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">
              Assalamu Alaikum, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-[#6B7280] mt-1">Welcome to your dashboard</p>
          </div>
          {children.length === 0 && (
            <Button
              asChild
              className="bg-[#E76F51] hover:bg-[#D65A3C] text-white rounded-full px-6"
              data-testid="add-first-child-btn"
            >
              <Link to="/children">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Child
              </Link>
            </Button>
          )}
        </div>

        {/* Empty State */}
        {children.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <div className="flex justify-center mb-6">
              <img 
                src={LOGO_URL} 
                alt="Alnoor Islamic Center" 
                className="h-24 w-24 object-contain opacity-80"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">
              Welcome to AIC Super Muslims Challenge!
            </h2>
            <p className="text-[#6B7280] mb-6 max-w-md mx-auto">
              Get started by adding your children. They'll be able to complete daily Islamic challenges, 
              earn points, and compete on the leaderboard!
            </p>
            <Button
              asChild
              className="bg-[#08513F] hover:bg-[#064031] text-white rounded-full px-8 py-6 text-lg"
              data-testid="empty-add-child-btn"
            >
              <Link to="/children">
                <Users className="w-5 h-5 mr-2" />
                Add Your First Child
              </Link>
            </Button>
          </Card>
        ) : (
          <>
            {/* Stats Grid - Bento Style */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Main Stats Card - Spans 2 columns */}
              <Card className="md:col-span-2 bg-islamic-green text-white rounded-3xl border-0 shadow-xl overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <img 
                      src={LOGO_URL} 
                      alt="Alnoor Islamic Center" 
                      className="h-14 w-14 object-contain bg-white/10 rounded-2xl p-2"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div>
                      <h2 className="text-2xl font-bold font-outfit">Family Stats</h2>
                      <p className="text-white/70">Your combined achievements</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Star className="w-6 h-6 text-[#D4AF37]" />
                        <span className="text-white/70">Total Points</span>
                      </div>
                      <p className="text-4xl font-bold font-outfit">{totalPoints}</p>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Flame className="w-6 h-6 text-[#E76F51]" />
                        <span className="text-white/70">Best Streak</span>
                      </div>
                      <p className="text-4xl font-bold font-outfit">{bestStreak} days</p>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle2 className="w-6 h-6 text-green-400" />
                        <span className="text-white/70">Completed Today</span>
                      </div>
                      <p className="text-4xl font-bold font-outfit">{approvedToday}</p>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-6 h-6 text-yellow-400" />
                        <span className="text-white/70">Pending</span>
                      </div>
                      <p className="text-4xl font-bold font-outfit">{pendingSubmissions}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions Card */}
              <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-[#264653] font-outfit">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    asChild
                    variant="outline"
                    className="w-full justify-between h-14 rounded-xl border-2 border-[#08513F]/20 hover:border-[#08513F] hover:bg-[#08513F]/5"
                    data-testid="quick-tasks-btn"
                  >
                    <Link to="/tasks">
                      <span className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#08513F]" />
                        Submit Task
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full justify-between h-14 rounded-xl border-2 border-[#D4AF37]/30 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5"
                    data-testid="quick-achievements-btn"
                  >
                    <Link to="/achievements">
                      <span className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#D4AF37]" />
                        View Achievements
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="w-full justify-between h-14 rounded-xl border-2 border-[#E76F51]/30 hover:border-[#E76F51] hover:bg-[#E76F51]/5"
                    data-testid="quick-leaderboard-btn"
                  >
                    <Link to="/leaderboard">
                      <span className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-[#E76F51]" />
                        Leaderboard
                      </span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Children Cards */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-[#264653] font-outfit">Your Children</h2>
                <Button
                  asChild
                  variant="outline"
                  className="rounded-full border-2 border-[#08513F] text-[#08513F] hover:bg-[#08513F] hover:text-white"
                  data-testid="add-child-btn"
                >
                  <Link to="/children">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Link>
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {children.map((child, index) => (
                  <ChildCard 
                    key={child.child_id}
                    child={child}
                    index={index}
                    rank={childRanks[child.child_id]}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
